"use client";
import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { activeLocales, localeNames, type Locale } from "@/i18n/config";
import Flag from "@/components/Flag";

// Globe simgesi + seçili dilin adı + ok. Tıklayınca açılır menü. Seçim
// NEXT_LOCALE cookie'sine yazılır ve sayfa yenilenir; next-intl yeni dili sunar.
// Landing'deki switcher ile aynı görünüm/davranış.
//
// Liste on bir dile çıktığı için menü artık kaydırılabilir ve seçili dil bir onay
// işaretiyle işaretlenir: on bir satırda "hangisi aktif" sorusunu yalnızca renk
// farkına bırakmak, kontrast ayarı düşük ekranlarda okunmaz oluyordu. Düğme dar
// ekranda dil ADI yerine KODU gösterir ("Nederlands" mobil başlıkta yer kaplıyordu).
//
// Menüdeki her satırın başında dilin ülke bayrağı durur (`components/Flag.tsx`):
// alfabetik on bir satır arasında doğru dili bulmak, yabancı bir arayüz dilinde
// sıkışıp kalan kullanıcı için okumaktan hızlıdır. Bayrak adın YERİNE değil
// YANINDA durur — bayrak dili değil ülkeyi gösterir, tek başına yeterli değildir.
// Tetik düğmesi küre simgesini korur: kapalıyken gösterilen şey "hangi ülke"
// değil "burası dil seçici" bilgisidir.
function Globe() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.5 3.8 5.7 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.7-3.8-9s1.3-6.5 3.8-9z" />
    </svg>
  );
}

export default function LanguageSwitcher({ className = "fixed top-5 right-5 z-40" }: { className?: string }) {
  const locale = useLocale() as Locale;
  // F23: erişilebilirlik etiketi de çevrilir (ayarlardaki "Dil" başlığıyla aynı anahtar).
  const label = useTranslations("settings.language")("title");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, []);

  function choose(next: Locale) {
    setOpen(false);
    if (next === locale) return;
    // eslint-disable-next-line react-hooks/immutability -- `document.cookie` bir setter'dır; kural bunu ref yazımı sanıyor (F20).
    document.cookie = `NEXT_LOCALE=${next}; path=/; max-age=31536000; samesite=lax`;
    window.location.reload();
  }

  return (
    <div ref={ref} className={className}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] shadow-sm transition-colors"
      >
        <Globe />
        <span className="hidden sm:inline">{localeNames[locale]}</span>
        <span className="sm:hidden uppercase tracking-wide">{locale}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 opacity-65 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          // max-h + overflow: on bir dil, kısa ekranlarda menüyü görünür alanın
          // dışına taşırıyordu; son diller tıklanamaz hâle geliyordu.
          className="absolute right-0 top-[calc(100%+6px)] min-w-[168px] max-h-[min(60vh,380px)] overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-lg z-50"
        >
          {activeLocales.map(l => (
            <button
              key={l}
              type="button"
              role="option"
              aria-selected={l === locale}
              onClick={() => choose(l)}
              className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-[13px] transition-colors ${
                l === locale
                  ? "bg-[var(--accent-light)] text-[var(--accent)] font-semibold"
                  : "text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] hover:text-[var(--text-primary)]"
              }`}
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <Flag locale={l} />
                <span className="truncate">{localeNames[l]}</span>
              </span>
              {l === locale && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" className="shrink-0" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
